import React, { useState, useMemo, useEffect } from 'react';
import { Paper, Title, Text, Stack, Table, Badge, Loader, Group, ScrollArea, Pagination, Tooltip, Select, Switch } from '@mantine/core';
import { IconHistory, IconFilter, IconWorld, IconClock } from '@tabler/icons-react';
import {
  useCrawlHistoryQuery,
  useRegionsQuery,
  useRedditSourcesQuery,
  CrawlHistoryDto,
  RegionDropdownDto,
  RedditSourceSummaryDto
} from '../../../../application/queries/crawlStatusQueries';

/**
 * RedditCrawlHistory Component
 *
 * Displays recent Reddit crawl history with details about each crawl execution.
 * Shows status, duration, posts processed, comments, and mentions found.
 * Sprint 027 SESSION_358: Added pagination and new efficiency metrics columns.
 * Sprint 027 SESSION_369: Added region dropdown and active/inactive toggle for source filtering.
 *
 * @example
 * <RedditCrawlHistory />
 */
// SESSION_369: Time range options for crawl history
const TIME_RANGE_OPTIONS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 3 days' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
];

export const RedditCrawlHistory: React.FC = (): React.ReactElement => {
  const [page, setPage] = useState(1);
  const [selectedSubredditId, setSelectedSubredditId] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);
  const [timeRangeHours, setTimeRangeHours] = useState<string>('24');
  const pageSize = 20;

  // SESSION_369: Fetch regions for dropdown
  const { data: regions, isLoading: regionsLoading, error: regionsError } = useRegionsQuery();

  // SESSION_369: Debug logging for regions query
  useEffect(() => {
    if (regionsError) {
      console.error('SESSION_369: Regions query error:', regionsError);
    }
    if (regions) {
      console.log('SESSION_369: Regions loaded:', regions.length, 'regions', regions);
    }
  }, [regions, regionsError]);

  // SESSION_369: Auto-select "Puget Sound" region on first load
  useEffect(() => {
    if (regions && regions.length > 0 && !selectedRegionId) {
      // Find "Puget Sound" region, fallback to first region
      const pugetSound = regions.find((r: RegionDropdownDto) =>
        r.name.toLowerCase().includes('puget') || r.name.toLowerCase().includes('seattle')
      );
      console.log('SESSION_369: Auto-selecting region:', pugetSound ?? regions[0]);
      setSelectedRegionId(pugetSound?.id ?? regions[0]?.id ?? null);
    }
  }, [regions, selectedRegionId]);

  // SESSION_369: Fetch Reddit sources for the selected region
  const { data: sources, error: sourcesError } = useRedditSourcesQuery(
    selectedRegionId,
    showActiveOnly ? true : undefined, // undefined = show all when toggle is off
    { enabled: !!selectedRegionId }
  );

  // SESSION_369: Debug logging for sources query
  useEffect(() => {
    if (sourcesError) {
      console.error('SESSION_369: Sources query error:', sourcesError);
    }
    if (sources) {
      console.log('SESSION_369: Sources loaded:', sources.length, 'sources for region', selectedRegionId);
    }
  }, [sources, sourcesError, selectedRegionId]);

  // SESSION_369: Derive subreddit options from sources query
  const subredditOptions = useMemo(() => {
    if (!sources) return [];
    const options = sources
      .map((source: RedditSourceSummaryDto) => ({
        value: source.id,
        label: `r/${source.subredditName}${!source.isActive ? ' (inactive)' : ''}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    console.log('SESSION_369: Computed subredditOptions:', options.length, 'options');
    return options;
  }, [sources]);

  // Region dropdown options
  const regionOptions = useMemo(() => {
    if (!regions) {
      console.log('SESSION_369: regionOptions - no regions data yet');
      return [];
    }
    const options = regions
      .map((r: RegionDropdownDto) => ({
        value: r.id,
        label: `${r.name} (${r.activeSourceCount} active)`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    console.log('SESSION_369: Computed regionOptions:', options.length, 'options', options);
    return options;
  }, [regions]);

  // Fetch crawl history with pagination and optional subreddit filter
  // SESSION_369: Added time range selector
  const { data, isLoading, isError } = useCrawlHistoryQuery(
    parseInt(timeRangeHours, 10),
    50,
    page,
    pageSize,
    selectedSubredditId ?? undefined,
    { refetchInterval: 30000 }
  );

  // Extract items from paginated response
  const history = data?.items ?? [];

  // Reset to page 1 when filter changes
  const handleSubredditChange = (value: string | null): void => {
    setSelectedSubredditId(value);
    setPage(1);
  };

  // SESSION_369: Handle region change - reset subreddit selection
  const handleRegionChange = (value: string | null): void => {
    setSelectedRegionId(value);
    setSelectedSubredditId(null);
    setPage(1);
  };

  // SESSION_369: Handle active toggle - reset subreddit selection
  const handleActiveToggle = (checked: boolean): void => {
    setShowActiveOnly(checked);
    setSelectedSubredditId(null);
    setPage(1);
  };

  // SESSION_369: Handle time range change
  const handleTimeRangeChange = (value: string | null): void => {
    if (value) {
      setTimeRangeHours(value);
      setPage(1);
    }
  };

  // SESSION_369: Get label for current time range
  const getTimeRangeLabel = (): string => {
    const option = TIME_RANGE_OPTIONS.find(o => o.value === timeRangeHours);
    return option?.label ?? 'Last 24 hours';
  };

  /**
   * Get badge color for crawl status
   */
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Completed':
        return 'green';
      case 'Failed':
        return 'red';
      case 'Started':
      case 'InProgress':
        return 'blue';
      default:
        return 'gray';
    }
  };

  /**
   * Format duration in seconds to human-readable string
   */
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  /**
   * Get the user's timezone, falling back to Seattle (Pacific) if unavailable
   */
  const userTimezone = useMemo((): string => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || 'America/Los_Angeles';
    } catch {
      return 'America/Los_Angeles';
    }
  }, []);

  /**
   * Format date/time in user's local timezone (or Seattle/Pacific as fallback)
   * Shows date and time in a compact format: "Jan 18, 2:14 PM"
   *
   * Sprint 027 SESSION_360: Fix UTC parsing - backend sends UTC times without "Z" suffix,
   * causing JavaScript to interpret them as local time. Append "Z" to ensure UTC parsing.
   */
  const formatLocalDateTime = (isoString: string): string => {
    try {
      // Backend sends UTC times without timezone indicator (e.g., "2026-01-19T01:22:00")
      // JavaScript interprets this as local time, which is incorrect.
      // Append "Z" if no timezone indicator present to force UTC interpretation.
      let utcString = isoString;
      if (!isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-', 10)) {
        utcString = isoString + 'Z';
      }
      const date = new Date(utcString);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date);
    } catch {
      return isoString;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Stack gap="xs" align="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading crawl history...
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Error state
  if (isError) {
    return (
      <Paper p="md" withBorder>
        <Stack gap="xs" align="center">
          <IconHistory size={32} stroke={1.5} color="var(--mantine-color-red-5)" />
          <Title order={4}>Error Loading History</Title>
          <Text size="sm" c="dimmed" ta="center">
            Failed to load crawl history. Please try again later.
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Empty state
  if (!history || history.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Stack gap="xs" align="center">
          <IconHistory size={32} stroke={1.5} color="var(--mantine-color-gray-5)" />
          <Title order={4}>No Crawl History</Title>
          <Text size="sm" c="dimmed" ta="center">
            No crawls have been executed in the last 24 hours.
            <br />
            The background service will start crawling automatically.
          </Text>
        </Stack>
      </Paper>
    );
  }

  /**
   * Format count with N/A for null (historical records)
   */
  const formatCount = (count: number | null): string => {
    return count === null ? 'N/A' : count.toString();
  };

  // Table with crawl history
  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs">
            <IconHistory size={20} stroke={1.5} />
            <Title order={4}>Crawl History</Title>
          </Group>
          <Text size="xs" c="dimmed">
            {getTimeRangeLabel()} {data && `(${data.totalCount}${selectedSubredditId ? ' filtered' : ''})`}
          </Text>
        </Group>

        {/* SESSION_369: Filter controls row */}
        <Group gap="sm" wrap="wrap">
          {/* Time range selector */}
          <Select
            data={TIME_RANGE_OPTIONS}
            value={timeRangeHours}
            onChange={handleTimeRangeChange}
            size="xs"
            w={140}
            leftSection={<IconClock size={14} />}
            styles={{
              input: { fontSize: '12px' },
            }}
            allowDeselect={false}
          />

          {/* Region dropdown */}
          <Select
            placeholder={regionsLoading ? "Loading regions..." : (regionsError ? "Error loading" : "Select region")}
            data={regionOptions}
            value={selectedRegionId}
            onChange={handleRegionChange}
            size="xs"
            w={180}
            leftSection={<IconWorld size={14} />}
            styles={{
              input: { fontSize: '12px' },
            }}
            disabled={regionsLoading}
          />

          {/* Active toggle */}
          <Switch
            label="Active only"
            checked={showActiveOnly}
            onChange={(e) => handleActiveToggle(e.currentTarget.checked)}
            size="xs"
          />

          {/* Subreddit filter */}
          <Select
            placeholder="All subreddits"
            data={subredditOptions}
            value={selectedSubredditId}
            onChange={handleSubredditChange}
            clearable
            searchable
            size="xs"
            w={180}
            leftSection={<IconFilter size={14} />}
            styles={{
              input: { fontSize: '12px' },
            }}
            disabled={!selectedRegionId}
          />

          {/* Source count info */}
          {sources && (
            <Text size="xs" c="dimmed">
              {sources.length} subreddit{sources.length !== 1 ? 's' : ''} available
            </Text>
          )}
        </Group>

        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Subreddit</Table.Th>
                <Table.Th>Time</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>
                  <Tooltip label="Actual distinct Reddit posts processed" withArrow>
                    <Text size="sm" fw={600}>Posts</Text>
                  </Tooltip>
                </Table.Th>
                <Table.Th>
                  <Tooltip label="Comments processed" withArrow>
                    <Text size="sm" fw={600}>Comments</Text>
                  </Tooltip>
                </Table.Th>
                <Table.Th>
                  <Tooltip label="Skipped duplicates (already processed)" withArrow>
                    <Text size="sm" fw={600}>Skipped</Text>
                  </Tooltip>
                </Table.Th>
                <Table.Th>Mentions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.map((crawl: CrawlHistoryDto) => (
                <Table.Tr key={crawl.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      r/{crawl.subredditName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {formatLocalDateTime(crawl.startedAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDuration(crawl.durationSeconds)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getStatusColor(crawl.status)} variant="light" size="sm">
                      {crawl.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip
                      label={`Legacy count (content sources): ${crawl.postsProcessed}`}
                      withArrow
                      disabled={crawl.actualRedditPostsProcessed === null}
                    >
                      <Text size="sm">
                        {formatCount(crawl.actualRedditPostsProcessed)}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatCount(crawl.commentsProcessed)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={crawl.skippedDuplicateCount && crawl.skippedDuplicateCount > 0 ? 'orange' : 'dimmed'}>
                      {formatCount(crawl.skippedDuplicateCount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500} c={crawl.mentionsFound > 0 ? 'teal' : undefined}>
                      {crawl.mentionsFound}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {/* Pagination controls */}
        {data && data.totalPages > 1 && (
          <Group justify="center">
            <Pagination
              value={page}
              onChange={setPage}
              total={data.totalPages}
              size="sm"
            />
          </Group>
        )}

        {data && data.totalCount > 0 && (
          <Text size="xs" c="dimmed" ta="center">
            Showing {history.length} of {data.totalCount} crawls (Page {data.page} of {data.totalPages})
          </Text>
        )}
      </Stack>
    </Paper>
  );
};
